/**
 * TripTabs.tsx
 *
 * Trip Tabs for multiple trips. On mobile uses
 * multiple tab components stacked on top of each
 * other so that there are always two tabs visible.
 */
import React, { useMemo } from "react";

import { useScreenSize } from "../../../../../globals/hooks";
import { TripCategory } from "../../../../../types";
import MobileTripTabs from "./components/MobileTripTabs";
import DesktopTripTabs from "./components/DesktopTripTabs";

type TripTabsProps = {
  trips: {
    tripCategory?: TripCategory;
    label?: string | null;
    returnTrip?: {
      label?: string | null;
    };
  }[];
  activeTripIndex: number;
  onActiveTripIndexChange: (index: number) => void;
};

const getTrimmedLabel = (label?: string | null) => label?.trim();

function TripTabs(props: TripTabsProps) {
  const { trips, activeTripIndex, onActiveTripIndexChange } = props;

  // hooks
  const { isMobileView } = useScreenSize();

  // derived state
  const isCharter = trips[0]?.tripCategory === TripCategory.Charter;

  // event handlers
  const handleTabSelect = (
    _: React.SyntheticEvent<Element, Event>,
    tabIndex: number
  ) => {
    onActiveTripIndexChange(tabIndex);
  };

  // derived state
  const tripTabs = useMemo(() => {
    let tripCount = 0;

    return trips.map((trip, index) => {
      const tripLabel = getTrimmedLabel(trip.label);
      const returnTripLabel = getTrimmedLabel(trip.returnTrip?.label);
      const defaultLabel = isCharter
        ? `DAY ${index + 1}`
        : `TRIP ${++tripCount}` + (trip.returnTrip ? ` & ${++tripCount}` : "");
      const label =
        tripLabel && returnTripLabel
          ? `${tripLabel} & ${returnTripLabel}`
          : tripLabel || returnTripLabel || defaultLabel;

      return {
        value: index,
        label,
      };
    });
  }, [isCharter, trips]);

  return isMobileView ? (
    <MobileTripTabs
      tripTabs={tripTabs}
      onTabSelect={handleTabSelect}
      activeTripIndex={activeTripIndex}
    />
  ) : (
    <DesktopTripTabs
      tripTabs={tripTabs}
      onTabSelect={handleTabSelect}
      activeTripIndex={activeTripIndex}
    />
  );
}

export default TripTabs;
